import { Box, Dialog, DialogContent, IconButton } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import React from "react";
import { isMobile } from "react-device-detect";
import { Drawer as VaulDrawer } from "vaul";
import useWindowSize from "../../hooks/useWindowSize";
import { BREAKPOINT } from "../_helpers";

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  desktopModalMobileDrawer?: boolean;
  hideCloseButton?: boolean;
  noPadding?: boolean;
  drawerBottomOffset?: string;
  drawerDirection?: "bottom" | "right";
  drawerMaxHeight?: string;
  drawerWidth?: string;
  paperSx?: object;
  bodySx?: object;
  footerSx?: object;
  showHandle?: boolean;
}

export function BaseModal({
  isOpen,
  onClose,
  children,
  footer,
  desktopModalMobileDrawer = false,
  hideCloseButton = false,
  noPadding = false,
  drawerBottomOffset = "calc(3.5rem + env(safe-area-inset-bottom, 0px))",
  drawerDirection = "bottom",
  drawerMaxHeight = "calc(100dvh - 7rem - env(safe-area-inset-bottom, 0px))",
  drawerWidth = "min(100vw, 425px)",
  paperSx,
  bodySx,
  footerSx,
  showHandle = true,
}: BaseModalProps) {
  const windowSize = useWindowSize();
  const reallyIsMobile = isMobile || windowSize.width <= BREAKPOINT;
  const isRightDrawer = drawerDirection === "right";
  const useDrawer = desktopModalMobileDrawer && (reallyIsMobile || isRightDrawer);

  if (useDrawer) {
    return (
      <VaulDrawer.Root
        open={isOpen}
        direction={drawerDirection}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
        shouldScaleBackground
        modal
      >
        <VaulDrawer.Portal>
          <VaulDrawer.Overlay
            onClick={onClose}
            style={{
              position: "fixed",
              insetInline: 0,
              top: 0,
              bottom: isRightDrawer ? 0 : drawerBottomOffset,
              backgroundColor: "rgba(1, 1, 1, 0.38)",
              zIndex: 10019,
            }}
          />
          <VaulDrawer.Content
            onClick={(event) => {
              if (!isRightDrawer && event.target === event.currentTarget) {
                onClose();
              }
            }}
            style={{
              position: "fixed",
              ...(isRightDrawer
                ? {
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: reallyIsMobile ? "100vw" : drawerWidth,
                    maxWidth: "100vw",
                    height: "100dvh",
                    maxHeight: "100dvh",
                  }
                : {
                    left: 0,
                    right: 0,
                    bottom: drawerBottomOffset,
                    height: drawerMaxHeight,
                    maxHeight: drawerMaxHeight,
                  }),
              zIndex: 10020,
              display: "flex",
              flexDirection: "column",
              justifyContent: isRightDrawer ? "initial" : "flex-end",
              backgroundColor: "transparent",
              boxShadow: "none",
              outline: "none",
              ...paperSx,
            }}
          >
            <VaulDrawer.Title
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0,0,0,0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              Dialog
            </VaulDrawer.Title>
            <VaulDrawer.Description
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0,0,0,0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              {isRightDrawer ? "Right drawer" : "Bottom drawer"}
            </VaulDrawer.Description>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                ...(isRightDrawer
                  ? {
                      height: "100%",
                      maxHeight: "100dvh",
                      borderTopLeftRadius: { xs: 0, md: "18px" },
                      borderBottomLeftRadius: { xs: 0, md: "18px" },
                    }
                  : {
                      maxHeight: drawerMaxHeight,
                      borderTopLeftRadius: "18px",
                      borderTopRightRadius: "18px",
                    }),
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                ...bodySx,
              }}
            >
              {showHandle && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    pt: "9px",
                    pb: "6px",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: "48px",
                      height: "5px",
                      borderRadius: "999px",
                      backgroundColor: "color-mix(in srgb, var(--logoColor) 50%, transparent)",
                      opacity: 0.78,
                    }}
                  />
                </Box>
              )}
              {!hideCloseButton && (
                <IconButton
                  onClick={onClose}
                  aria-label="Close"
                  sx={{
                    position: "absolute",
                    top: "1rem",
                    right: "1rem",
                    zIndex: 3,
                    color: "inherit",
                  }}
                >
                  <X style={{ width: "1.25em", height: "1.25em" }} />
                </IconButton>
              )}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  p: noPadding ? 0 : "1rem",
                }}
                data-lenis-prevent
              >
                {children}
              </Box>
              {footer && <Box sx={{ flexShrink: 0, ...footerSx }}>{footer}</Box>}
            </Box>
          </VaulDrawer.Content>
        </VaulDrawer.Portal>
      </VaulDrawer.Root>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      // Same layer as the drawer. MUI's default (1300) sits under ArtistNav
      // (9999) and the arcade's focus mode (10015).
      sx={{ zIndex: 10020 }}
      PaperProps={{ sx: { backgroundColor: "transparent", boxShadow: "none", ...paperSx } }}
    >
      <AnimatePresence>
        {isOpen && (
          <DialogContent sx={{ p: 0 }}>
            <Box
              component={motion.div}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ type: "tween", duration: 0.12 }}
              sx={bodySx}
            >
              {!hideCloseButton && (
                <IconButton
                  onClick={onClose}
                  aria-label="Close"
                  sx={{
                    position: "absolute",
                    top: "1rem",
                    right: "1rem",
                    zIndex: 3,
                    color: "inherit",
                  }}
                >
                  <X style={{ width: "1.25em", height: "1.25em" }} />
                </IconButton>
              )}
              {children}
              {footer && <Box sx={footerSx}>{footer}</Box>}
            </Box>
          </DialogContent>
        )}
      </AnimatePresence>
    </Dialog>
  );
}

export default BaseModal;
